import { useMemo } from 'react';
import type { MembershipDateRange } from './constants';
import {
  isMembershipCancelled,
  isMembershipExpired,
  isMembershipUpcoming,
} from './status';
import {
  getActiveMemberships,
  getMembershipsToDisplay,
  selectMostRelevantMembership,
  type DisplayMembershipsResult,
} from './sort';

/**
 * Memoized membership derivations for a list of memberships.
 * Centralizes the common breakdowns (active / upcoming / expired / cancelled)
 * and the "most relevant" selection used by cards and list views.
 */
export const useMembershipLogic = <T extends MembershipDateRange>(
  memberships: T[] | undefined | null,
  referenceDate?: Date
) => {
  return useMemo(() => {
    const safeMemberships = memberships || [];
    const activeMemberships = getActiveMemberships(safeMemberships, referenceDate);
    const mostRelevantMembership = selectMostRelevantMembership(safeMemberships, referenceDate);

    return {
      activeMemberships,
      mostRelevantMembership,
      hasMultipleActive: activeMemberships.length > 1,
      hasAnyMembership: safeMemberships.length > 0,
      totalMemberships: safeMemberships.length,
      upcomingMemberships: safeMemberships.filter(m => isMembershipUpcoming(m, referenceDate)),
      expiredMemberships: safeMemberships.filter(m => isMembershipExpired(m, referenceDate)),
      cancelledMemberships: safeMemberships.filter(m => isMembershipCancelled(m)),
      validMemberships: safeMemberships.filter(m => !isMembershipCancelled(m)),
    };
  }, [memberships, referenceDate]);
};

/**
 * Memoized wrapper around `getMembershipsToDisplay` — the single source of truth
 * for "which memberships should this UI surface show for a member?".
 * See getMembershipsToDisplay for the rule.
 */
export const useMembershipsToDisplay = <T extends MembershipDateRange>(
  memberships: T[] | undefined | null,
  referenceDate?: Date
): DisplayMembershipsResult<T> => {
  return useMemo(
    () => getMembershipsToDisplay(memberships, referenceDate),
    [memberships, referenceDate]
  );
};
